import React from "react";

import PageHero from "components/site/PageHero";
import RevealBlock from "components/site/RevealBlock";
import { Col, Container, Row } from "components/site/SitePrimitives";
import SiteShell from "components/site/SiteShell";
import {
  approachPage,
  buildStructuredData,
} from "content/siteContent";
import usePageMeta from "hooks/usePageMeta";

function Approach() {
  usePageMeta({
    ...approachPage.meta,
    structuredData: buildStructuredData({
      pageName: "Approach",
      description: approachPage.meta.description,
      path: approachPage.meta.path,
    }),
  });

  return (
    <SiteShell pageClassName="ignitum-site-page ignitum-approach-page">
      <PageHero
        aside={
          <RevealBlock variant="card">
            <p className="site-panel-label">Working assumptions</p>
            <ul className="site-signal-list">
              {approachPage.hero.signals.map((signal) => (
                <li key={signal}>{signal}</li>
              ))}
            </ul>
          </RevealBlock>
        }
        compact
        description={approachPage.hero.description}
        eyebrow={approachPage.hero.eyebrow}
        title={approachPage.hero.title}
      />

      <section className="site-section">
        <Container>
          <RevealBlock className="site-section-intro" variant="lift">
            <p className="site-eyebrow">Why this matters</p>
            <h2>Novel work still has to survive reality.</h2>
          </RevealBlock>
          <Row className="site-grid-gap">
            {approachPage.opening.map((paragraph, index) => (
              <Col key={paragraph} lg="6">
                <RevealBlock variant={index === 0 ? "slide-left" : "slide-right"}>
                  <div className="site-text-block">
                    <p>{paragraph}</p>
                  </div>
                </RevealBlock>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section className="site-section site-section-muted">
        <Container>
          <RevealBlock className="site-section-intro" variant="lift">
            <p className="site-eyebrow">Principles</p>
            <h2>Principles that keep the work grounded</h2>
          </RevealBlock>
          <Row className="site-card-grid">
            {approachPage.principles.map((item, index) => (
              <Col key={item.title} md="6">
                <RevealBlock delay={index * 65} variant="card">
                  <div className="site-card">
                    <p className="site-card-label">{item.label}</p>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </div>
                </RevealBlock>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section className="site-section">
        <Container>
          <RevealBlock className="site-section-intro" variant="lift">
            <p className="site-eyebrow">When this matters</p>
            <h2>{approachPage.relevance.title}</h2>
            <p>{approachPage.relevance.description}</p>
          </RevealBlock>
          <Row className="site-card-grid">
            {approachPage.relevance.items.map((item, index) => (
              <Col key={item.title} md="4">
                <RevealBlock delay={index * 70} variant="card">
                  <div className="site-card">
                    <p className="site-card-label">{item.label}</p>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </div>
                </RevealBlock>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section className="site-section site-section-muted">
        <Container>
          <RevealBlock className="site-section-intro" variant="lift">
            <p className="site-eyebrow">R&amp;D and deployment</p>
            <h2>{approachPage.comparison.title}</h2>
            <p>{approachPage.comparison.description}</p>
          </RevealBlock>
          <Row className="site-card-grid">
            {approachPage.comparison.items.map((item, index) => (
              <Col key={item.title} lg="6">
                <RevealBlock
                  delay={index * 80}
                  variant={index === 0 ? "slide-left" : "slide-right"}
                >
                  <div className="site-card site-split-card">
                    <h3>{item.title}</h3>
                    <div className="site-kv">
                      {item.rows.map((row) => (
                        <div className="site-kv-row" key={row.label}>
                          <strong>{row.label}</strong>
                          <p>{row.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </RevealBlock>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section className="site-section">
        <Container>
          <RevealBlock className="site-section-intro" variant="lift">
            <p className="site-eyebrow">Process</p>
            <h2>{approachPage.process.title}</h2>
            <p>{approachPage.process.description}</p>
          </RevealBlock>
          <Row className="site-card-grid">
            {approachPage.process.steps.map((step, index) => (
              <Col key={step.title} md="4">
                <RevealBlock delay={index * 70} variant="card">
                  <div className="site-card site-step-card">
                    <span className="site-step-number">0{index + 1}</span>
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                </RevealBlock>
              </Col>
            ))}
          </Row>
        </Container>
      </section>
    </SiteShell>
  );
}

export default Approach;
